import type { ActionItem } from '../../../redux/EditorSlice';
import type { FrameworkAction, FrameworkArgument } from '../../../framework/framework.d';
import {
  isFrameworkArgOptionalNoneDefault,
  isPlainFrameworkNumericLiteral,
  shouldPreserveRawDriveUnitPortArgument,
  shouldPreserveRawFrameworkBoolArgument,
  shouldPreserveRawFrameworkElectronicArgument,
  shouldPreserveRawFrameworkEnumArgument,
  shouldPreserveRawFrameworkNumericArgument,
  shouldPreserveRawFrameworkStringArgument,
} from '../../../utils/frameworkArgTypes';
import { escapePythonStringLiteral, isPythonIdentifierPath, shouldQuotePythonActionArg } from '../../../utils/pythonActionFormatting';
import { isSequenceTupleArg, isTupleArg, sequenceTupleMemberValueKey, sequenceTupleRowIndices, tupleMemberValueKey } from '../../../utils/tupleArg';
import {
  AUTONOMOUS_ROUTINES_REGION_START,
  findRegionSpanLineIndices,
  isAutonomousRoutinesRegionStartLine,
  isRegionInteriorEmpty,
  REGION_END,
} from '../../../utils/programRegionMarkers';
import {
  extractCallArgsText as extractCallArgsTextFromSource,
  findTopLevelKeywordAssignmentIndex,
  isPlainPythonQuotedStringLiteral,
  splitTopLevelByComma as splitTopLevelByCommaFromSource,
} from '../../../utils/pythonSourceScanUtils';
import { formatGeneratedPythonCall } from '../../../utils/pythonFormattingUtils';

export type { ExtractedCallArgsText } from '../../../utils/pythonSourceScanUtils';

export const DO_NOTHING_AUTONOMOUS_ROUTINE_ID = '__do_nothing__';
export const UI_AUTONOMOUS_ROUTINES_REGION_START = AUTONOMOUS_ROUTINES_REGION_START;
export const UI_AUTONOMOUS_ROUTINES_REGION_END = REGION_END;
const PYTHON_RESERVED_WORDS = new Set([
  'False',
  'None',
  'True',
  'and',
  'as',
  'assert',
  'async',
  'await',
  'break',
  'class',
  'continue',
  'def',
  'del',
  'elif',
  'else',
  'except',
  'finally',
  'for',
  'from',
  'global',
  'if',
  'import',
  'in',
  'is',
  'lambda',
  'nonlocal',
  'not',
  'or',
  'pass',
  'raise',
  'return',
  'try',
  'while',
  'with',
  'yield',
]);

export const AUTONOMOUS_GROUP_TYPES = ['SequentialActionGroup', 'ParallelActionGroup', 'ParallelRaceGroup', 'ParallelDeadlineGroup'] as const;

export type AutonomousGroupType = (typeof AUTONOMOUS_GROUP_TYPES)[number];

export type AutonomousRoutineStep =
  | {
      kind: 'action';
      selection: string;
      argValues: Record<string, string>;
    }
  | {
      kind: 'group';
      groupType: AutonomousGroupType;
      children: AutonomousRoutineStep[];
    };

export interface AutonomousRoutineOption {
  id: string;
  identifier: string;
  label: string;
  source: 'ui-owned' | 'code-only';
  editable: boolean;
  deletable: boolean;
  duplicable: boolean;
  composition: AutonomousRoutineStep | null;
  expression: string;
  referenceExpression?: string;
  startLineIndex: number;
  endLineIndex: number;
}

export interface ActionLabelParts {
  parentName: string;
  mechanismName: string | undefined;
  actionName: string;
}

export interface AutonomousExpressionParseResult {
  selection: string;
  argValues: Record<string, string>;
  rawExpression?: string;
}

const RAW_ACTION_ARG_VALUES_KEY = '__bpsRawActionArgValues';
const RAW_ACTION_ARG_ORDER_KEY = '__bpsRawActionArgOrder';

type RawActionArgValues = Record<string, string>;
type RawActionArgCopyEntry = string | { sourceKey: string; targetKey: string };

function rawActionArgValues(argValues: Record<string, string> | undefined): RawActionArgValues | undefined {
  return (argValues as unknown as { [RAW_ACTION_ARG_VALUES_KEY]?: RawActionArgValues } | undefined)?.[RAW_ACTION_ARG_VALUES_KEY];
}

function rawActionArgOrder(argValues: Record<string, string> | undefined): string[] | undefined {
  const order = (argValues as unknown as { [RAW_ACTION_ARG_ORDER_KEY]?: string[] } | undefined)?.[RAW_ACTION_ARG_ORDER_KEY];
  return Array.isArray(order) ? order : undefined;
}

export function setRawActionArgOrder(argValues: Record<string, string>, argNames: string[]): void {
  const ordered = [...new Set(argNames.map((name) => String(name || '').trim()).filter(Boolean))];
  if (ordered.length === 0) return;
  Object.defineProperty(argValues, RAW_ACTION_ARG_ORDER_KEY, {
    configurable: true,
    enumerable: false,
    value: ordered,
  });
}

export function setRawActionArgValue(argValues: Record<string, string>, argName: string, rawValue: string): void {
  const rawValues = rawActionArgValues(argValues) ?? {};
  rawValues[argName] = rawValue;
  Object.defineProperty(argValues, RAW_ACTION_ARG_VALUES_KEY, {
    configurable: true,
    enumerable: false,
    value: rawValues,
  });
}

export function getRawActionArgValue(argValues: Record<string, string> | undefined, argName: string): string | undefined {
  const rawValue = rawActionArgValues(argValues)?.[argName];
  return typeof rawValue === 'string' ? rawValue : undefined;
}

export function getRawActionArgOrder(argValues: Record<string, string> | undefined): string[] {
  return [...(rawActionArgOrder(argValues) ?? [])];
}

/** Compares persisted action values, including their non-enumerable raw source and order metadata. */
export function actionArgValuesAreEqual(first: Record<string, string> | undefined, second: Record<string, string> | undefined): boolean {
  const firstEntries = Object.entries(first ?? {});
  const secondEntries = Object.entries(second ?? {});
  if (firstEntries.length !== secondEntries.length) return false;
  if (firstEntries.some(([key, value]) => !Object.prototype.hasOwnProperty.call(second ?? {}, key) || second?.[key] !== value)) return false;

  const firstRawEntries = Object.entries(rawActionArgValues(first) ?? {});
  const secondRawEntries = Object.entries(rawActionArgValues(second) ?? {});
  if (firstRawEntries.length !== secondRawEntries.length) return false;
  if (firstRawEntries.some(([key, value]) => rawActionArgValues(second)?.[key] !== value)) return false;

  const firstOrder = rawActionArgOrder(first) ?? [];
  const secondOrder = rawActionArgOrder(second) ?? [];
  return firstOrder.length === secondOrder.length && firstOrder.every((argName, index) => secondOrder[index] === argName);
}

export function clearRawActionArgValue(argValues: Record<string, string>, argName: string): void {
  const rawValues = rawActionArgValues(argValues);
  if (!rawValues) return;
  delete rawValues[argName];
}

export function copyRawActionArgValues(
  source: Record<string, string> | undefined,
  target: Record<string, string>,
  argNames: RawActionArgCopyEntry[]
): Record<string, string> {
  const copyEntries = argNames.map((entry) => (typeof entry === 'string' ? { sourceKey: entry, targetKey: entry } : entry));
  for (const { sourceKey, targetKey } of copyEntries) {
    const rawValue = getRawActionArgValue(source, sourceKey);
    if (rawValue !== undefined) setRawActionArgValue(target, targetKey, rawValue);
  }
  const sourceOrder = rawActionArgOrder(source);
  if (sourceOrder?.length) {
    const sourceToTarget = new Map(copyEntries.map(({ sourceKey, targetKey }) => [sourceKey, targetKey]));
    const ordered = sourceOrder.map((sourceKey) => sourceToTarget.get(sourceKey)).filter((targetKey): targetKey is string => Boolean(targetKey));
    const extras = copyEntries
      .map(({ targetKey }) => targetKey)
      .filter((targetKey) => Object.prototype.hasOwnProperty.call(target, targetKey) && !ordered.includes(targetKey));
    setRawActionArgOrder(target, [...ordered, ...extras]);
  }
  return target;
}

export function cloneRawActionArgValues(source: Record<string, string> | undefined): Record<string, string> {
  const target = { ...(source ?? {}) };
  return copyRawActionArgValues(source, target, [...Object.keys(source ?? {}), ...getRawActionArgOrder(source)]);
}

export function updateRawActionArgValue(source: Record<string, string> | undefined, argName: string, value: string): Record<string, string> {
  const target = cloneRawActionArgValues(source);
  target[argName] = value;
  clearRawActionArgValue(target, argName);
  return target;
}

/** Removes action values together with their preserved raw source and ordering metadata. */
export function removeActionArgValues(source: Record<string, string> | undefined, argNames: Iterable<string>): Record<string, string> {
  const removedNames = new Set(argNames);
  const target = cloneRawActionArgValues(source);
  removedNames.forEach((argName) => {
    delete target[argName];
    clearRawActionArgValue(target, argName);
  });
  const order = rawActionArgOrder(target);
  if (order) {
    Object.defineProperty(target, RAW_ACTION_ARG_ORDER_KEY, {
      configurable: true,
      enumerable: false,
      value: order.filter((argName) => !removedNames.has(argName)),
    });
  }
  return target;
}

export function parseActionLabel(label: string): ActionLabelParts {
  const parts = (label || '').split('.');
  const parentName = parts[0] || '';
  const actionName = parts[parts.length - 1] || '';
  // For 2-part labels, treat as parent.action (no duplicated mechanism segment).
  const mechanismName = parts.length >= 3 ? parts[1] : undefined;
  return { parentName, mechanismName, actionName };
}

export function buildActionLabel(parts: string[]): string {
  return parts.filter((segment, idx) => idx === 0 || segment !== parts[idx - 1]).join('.');
}

export function findActionItemForLabel(globalActionList: ActionItem[], label: string): ActionItem | null {
  if (!label) return null;
  const parts = label.split('.');
  const parentName = parts[0] || '';
  const actionName = parts[parts.length - 1] || '';
  const mechanismName = parts.length >= 3 ? parts[1] : undefined;
  let match = globalActionList.find(
    (item) =>
      (item.parentName || '') === parentName && (item.mechanismName || undefined) === (mechanismName || undefined) && item.action?.name === actionName
  );
  if (!match && parts.length === 2 && parentName) {
    match = globalActionList.find(
      (item) =>
        (item.parentName || '') === parentName &&
        ((item.mechanismName || undefined) === undefined || (item.mechanismName || '') === parentName) &&
        item.action?.name === actionName
    );
  }
  if (!match && parts.length === 2 && parentName === 'UtilityActions') {
    match = globalActionList.find((item) => item.action?.standalone === true && item.action?.name === actionName);
  }
  if (!match && parts.length === 1 && actionName) {
    match = globalActionList.find((item) => item.action?.standalone === true && item.action?.name === actionName);
  }
  return match ?? null;
}

export function findActionDefForLabel(globalActionList: ActionItem[], label: string): FrameworkAction | null {
  return findActionItemForLabel(globalActionList, label)?.action || null;
}

const HIDDEN_RUNTIME_ACTION_ARG_KEY = '__bpsHiddenRuntimeActionArg';

type HiddenRuntimeFrameworkArgument = FrameworkArgument & { [HIDDEN_RUNTIME_ACTION_ARG_KEY]?: true };

function hiddenRuntimeArg(arg: FrameworkArgument): FrameworkArgument {
  const next = { ...arg };
  Object.defineProperty(next, HIDDEN_RUNTIME_ACTION_ARG_KEY, {
    configurable: false,
    enumerable: false,
    value: true,
  });
  return next;
}

export function isHiddenRuntimeActionArg(arg: FrameworkArgument | null | undefined): boolean {
  return Boolean((arg as HiddenRuntimeFrameworkArgument | null | undefined)?.[HIDDEN_RUNTIME_ACTION_ARG_KEY]);
}

const AUTO_ZERO_HIDDEN_RUNTIME_ARGS_BY_ACTION_NAME: Record<string, FrameworkArgument[]> = {
  auto_zero_action: [
    hiddenRuntimeArg({ name: 'seek_max_torque', type: 'float', defaultValue: null }),
    hiddenRuntimeArg({ name: 'current_spike_amps', type: 'float', min: 0, defaultValue: 2.0 }),
    hiddenRuntimeArg({ name: 'settling_period_ms', type: 'int', min: 0, defaultValue: 200 }),
    hiddenRuntimeArg({ name: 'baseline_period_ms', type: 'int', min: 0, defaultValue: 200 }),
    hiddenRuntimeArg({ name: 'position_stall_deg', type: 'float', min: 0, defaultValue: 1.0 }),
    hiddenRuntimeArg({ name: 'position_stall_period_ms', type: 'int', min: 0, defaultValue: 300 }),
  ],
  auto_zero_with_sensor_action: [hiddenRuntimeArg({ name: 'seek_max_torque', type: 'float', defaultValue: null })],
  auto_zero_with_min_sensor_action: [hiddenRuntimeArg({ name: 'seek_max_torque', type: 'float', defaultValue: null })],
  auto_zero_with_max_sensor_action: [hiddenRuntimeArg({ name: 'seek_max_torque', type: 'float', defaultValue: null })],
};

const AUTO_ZERO_RUNTIME_ARG_ORDER_BY_ACTION_NAME: Record<string, string[]> = {
  auto_zero_action: [
    'reset_to',
    'reset_units',
    'reset_source',
    'seek_velocity_percent',
    'seek_max_torque',
    'current_spike_amps',
    'settling_period_ms',
    'baseline_period_ms',
    'timeout_ms',
    'position_stall_deg',
    'position_stall_period_ms',
  ],
  auto_zero_with_sensor_action: [
    'sensor',
    'reset_to',
    'reset_units',
    'reset_source',
    'seek_velocity_percent',
    'seek_max_torque',
    'timeout_ms',
    'invert_sensor',
    'distance_criteria',
    'is_less_than',
  ],
  auto_zero_with_min_sensor_action: ['reset_to', 'reset_units', 'reset_source', 'seek_velocity_percent', 'seek_max_torque', 'timeout_ms'],
  auto_zero_with_max_sensor_action: ['reset_to', 'reset_units', 'reset_source', 'seek_velocity_percent', 'seek_max_torque', 'timeout_ms'],
};

export function withHiddenRuntimeActionArgs(actionDef: FrameworkAction | null | undefined): FrameworkAction | null {
  if (!actionDef) return null;
  const runtimeOrder = AUTO_ZERO_RUNTIME_ARG_ORDER_BY_ACTION_NAME[actionDef.name];
  if (!runtimeOrder) return actionDef;
  const args = actionDef.arguments ?? [];
  const argsByName = new Map(args.map((arg) => [String(arg.name || '').trim(), arg as FrameworkArgument]));
  const hiddenArgsByName = new Map((AUTO_ZERO_HIDDEN_RUNTIME_ARGS_BY_ACTION_NAME[actionDef.name] ?? []).map((arg) => [arg.name, arg]));
  const orderedArgs = runtimeOrder.flatMap((argName): FrameworkArgument[] => {
    const existingArg = argsByName.get(argName);
    if (existingArg) return [existingArg];
    const hiddenArg = hiddenArgsByName.get(argName);
    return hiddenArg ? [hiddenArg] : [];
  });
  const runtimeArgNames = new Set(runtimeOrder);
  const extraArgs = args.filter((arg) => !runtimeArgNames.has(String(arg.name || '').trim()));
  const nextArgs = [...orderedArgs, ...extraArgs];

  if (nextArgs.length === args.length && nextArgs.every((arg, index) => arg === args[index])) {
    return actionDef;
  }

  return { ...actionDef, arguments: nextArgs };
}

export const splitTopLevelByComma = splitTopLevelByCommaFromSource;

export interface ProgramStartActionCall {
  startLineIndex: number;
  endLineIndex: number;
  argsText: string;
}

const PROGRAM_START_ACTION_CALL_LINE_RE = /^\s*UtilityActions\s*\.\s*add_action_at_program_start\s*\(/;

export const extractCallArgsText = extractCallArgsTextFromSource;

const ACTION_CALL_EXPRESSION_RE = /^([A-Za-z_][\w]*(?:\.[A-Za-z_][\w]*)*)\s*\(([\s\S]*)\)\s*$/;
const PYTHON_IDENTIFIER_RE = /^[A-Za-z_][\w]*$/;

function trimSingleActionExpressionTrailingComma(raw: string): string {
  const value = (raw || '').trim();
  if (!value.endsWith(',')) return value;

  const withoutComma = value.slice(0, -1).trimEnd();
  return ACTION_CALL_EXPRESSION_RE.test(withoutComma) ? withoutComma : value;
}

export function findProgramStartActionCalls(lines: string[]): ProgramStartActionCall[] {
  const results: ProgramStartActionCall[] = [];
  let lineIndex = 0;
  while (lineIndex < lines.length) {
    const line = lines[lineIndex] ?? '';
    const callStartIndex = line.search(PROGRAM_START_ACTION_CALL_LINE_RE);
    if (callStartIndex < 0) {
      lineIndex++;
      continue;
    }

    const openParenIndex = line.indexOf('(', callStartIndex);
    if (openParenIndex < 0) {
      lineIndex++;
      continue;
    }

    const call = extractCallArgsText(lines, lineIndex, openParenIndex);
    if (call) {
      results.push({
        startLineIndex: lineIndex,
        endLineIndex: call.endLineIndex,
        argsText: call.argsText,
      });
      lineIndex = call.endLineIndex + 1;
    } else {
      lineIndex++;
    }
  }
  return results;
}

export function parseActionExpression(raw: string): { selection: string; argValues: Record<string, string> } {
  const value = trimSingleActionExpressionTrailingComma(raw);
  if (!value) return { selection: '', argValues: {} };
  const callMatch = value.match(ACTION_CALL_EXPRESSION_RE);
  if (callMatch) {
    const selection = callMatch[1];
    const argValues: Record<string, string> = {};
    const argOrder: string[] = [];
    const argsRaw = callMatch[2].trim();
    if (argsRaw.length > 0) {
      splitTopLevelByComma(argsRaw, { ignoreLineComments: true }).forEach((entry, index) => {
        const eq = findTopLevelKeywordAssignmentIndex(entry);
        if (eq > -1) {
          const key = entry.slice(0, eq).trim();
          const rawRhs = entry.slice(eq + 1).trim();
          let rhs = rawRhs;
          if (isPlainPythonQuotedStringLiteral(rhs)) {
            rhs = rhs.slice(1, -1);
            if (key) setRawActionArgValue(argValues, key, rawRhs);
          } else if (key) {
            setRawActionArgValue(argValues, key, rawRhs);
          }
          if (key) {
            argValues[key] = rhs;
            argOrder.push(key);
          }
        } else {
          const positionalKey = `arg${index}`;
          const rawValue = entry.trim();
          argValues[positionalKey] = rawValue;
          setRawActionArgValue(argValues, positionalKey, rawValue);
          argOrder.push(positionalKey);
        }
      });
    }
    setRawActionArgOrder(argValues, argOrder);
    return { selection, argValues };
  }
  const identMatch = value.match(/^([A-Za-z_][\w]*)\s*$/);
  if (identMatch) return { selection: `Custom.${identMatch[1]}`, argValues: {} };
  return { selection: '', argValues: {} };
}

function formatPythonArgValue(rawValue: string, shouldQuote: boolean): string {
  if (!shouldQuote && isPlainFrameworkNumericLiteral(rawValue)) return rawValue;
  if (!shouldQuote && /^(True|False|None)$/i.test(rawValue)) {
    const v = rawValue.toLowerCase();
    return v === 'true' ? 'True' : v === 'false' ? 'False' : 'None';
  }
  if (!shouldQuote && isPythonIdentifierPath(rawValue)) return rawValue;
  return `"${escapePythonStringLiteral(rawValue)}"`;
}

/**
 * Formats one sequence-tuple member, preserving raw expressions the same way scalar and tuple
 * arguments do (e.g. `get_text()` stays a call, not a `"get_text()"` string literal); plain
 * literals are quoted/coerced normally.
 */
function formatSequenceTupleMemberValue(
  member: FrameworkArgument,
  raw: string,
  actionName: string | undefined,
  rawSourceValue: string | undefined
): string {
  if (
    shouldPreserveRawDriveUnitPortArgument(member, raw) ||
    shouldPreserveRawFrameworkNumericArgument(member, raw) ||
    shouldPreserveRawFrameworkBoolArgument(member, raw, rawSourceValue) ||
    shouldPreserveRawFrameworkElectronicArgument(member, raw, rawSourceValue) ||
    shouldPreserveRawFrameworkEnumArgument(member, raw) ||
    shouldPreserveRawFrameworkStringArgument(member, raw, actionName, rawSourceValue)
  ) {
    return rawSourceValue || raw;
  }
  return formatPythonArgValue(raw, shouldQuotePythonActionArg({ name: member.name, type: member.type }, actionName));
}

function formatSequenceTupleArgValue(seqArg: FrameworkArgument, argValues: Record<string, string>, actionName?: string): string {
  const members = (seqArg.members ?? []) as FrameworkArgument[];
  const rows = sequenceTupleRowIndices(seqArg.name, argValues).map((rowIndex) => {
    const formattedMembers = members.map((member) => {
      const memberKey = sequenceTupleMemberValueKey(seqArg.name, rowIndex, member.name);
      // Preserve leading/trailing whitespace for string-like members (it's meaningful screen output);
      // trim numeric/enum members so an incidental `" 1 "` still serializes as `1`, not `" 1 "`.
      const rawValue = String(argValues[memberKey] ?? '');
      const raw = shouldQuotePythonActionArg({ name: member.name, type: member.type }, actionName) ? rawValue : rawValue.trim();
      return formatSequenceTupleMemberValue(member, raw, actionName, getRawActionArgValue(argValues, memberKey));
    });
    return `(${formattedMembers.join(', ')})`;
  });
  return `[${rows.join(', ')}]`;
}

function formatKnownActionArg(
  arg: Pick<FrameworkArgument, 'name' | 'type' | 'min' | 'max'>,
  rawValue: string,
  shouldQuote: boolean,
  actionName?: string,
  rawSourceValue?: string
): string {
  const argName = arg.name;
  if (rawValue.length === 0) return `${argName}=`;
  if (rawSourceValue !== undefined) return `${argName}=${String(rawSourceValue).trim()}`;
  if (
    shouldPreserveRawDriveUnitPortArgument(arg, rawValue) ||
    shouldPreserveRawFrameworkNumericArgument(arg, rawValue) ||
    shouldPreserveRawFrameworkBoolArgument(arg, rawValue, rawSourceValue) ||
    shouldPreserveRawFrameworkElectronicArgument(arg, rawValue, rawSourceValue) ||
    shouldPreserveRawFrameworkEnumArgument(arg, rawValue) ||
    shouldPreserveRawFrameworkStringArgument(arg, rawValue, actionName, rawSourceValue)
  ) {
    return `${argName}=${rawSourceValue || rawValue}`;
  }
  return `${argName}=${formatPythonArgValue(rawValue, shouldQuote)}`;
}

function formatTupleActionArg(arg: FrameworkArgument, argValues: Record<string, string>, actionName: string, index: number): string | null {
  const positionalKey = `arg${index}`;
  const rawSourceValue = getRawActionArgValue(argValues, arg.name) ?? getRawActionArgValue(argValues, positionalKey);
  if (rawSourceValue !== undefined) return `${arg.name}=${rawSourceValue.trim()}`;

  const hasNamedValue = Object.prototype.hasOwnProperty.call(argValues ?? {}, arg.name);
  const rawTupleValue = hasNamedValue ? String(argValues[arg.name] ?? '').trim() : '';
  if (rawTupleValue.length > 0) return `${arg.name}=${rawTupleValue}`;

  const memberValues = (arg.members || []).map((member) => {
    const memberKey = tupleMemberValueKey(arg.name, member.name);
    if (!Object.prototype.hasOwnProperty.call(argValues ?? {}, memberKey)) return null;
    const rawValue = String(argValues[memberKey] ?? '').trim();
    const rawMemberSourceValue = getRawActionArgValue(argValues, memberKey);
    return formatKnownActionArg(
      member,
      rawValue,
      shouldQuotePythonActionArg({ name: member.name, type: member.type }, actionName),
      actionName,
      rawMemberSourceValue
    ).slice(member.name.length + 1);
  });
  if (!memberValues.some((memberValue) => memberValue !== null)) return null;
  return `${arg.name}=(${memberValues.map((memberValue) => memberValue ?? '').join(', ')})`;
}

function formatUnknownActionArg(argName: string, rawValue: string, argValues: Record<string, string>, actionName: string): string {
  return formatKnownActionArg(
    { name: argName, type: '' },
    String(rawValue ?? '').trim(),
    shouldQuotePythonActionArg({ name: argName, type: '' }, actionName),
    actionName,
    getRawActionArgValue(argValues, argName)
  );
}

export function parseAutonomousExpression(raw: string): AutonomousExpressionParseResult {
  const value = trimSingleActionExpressionTrailingComma(raw);
  const parsed = parseActionExpression(value);
  if (!value) return parsed;
  if (ACTION_CALL_EXPRESSION_RE.test(value)) {
    return { ...parsed, rawExpression: value };
  }
  if (parsed.selection.startsWith('Custom.')) return parsed;
  return { ...parsed, rawExpression: value };
}

export function formatActionExpression(
  selection: string,
  argValues: Record<string, string>,
  findActionDef: (label: string) => FrameworkAction | null,
  opts: { unsupportedCode?: string } = {}
): string {
  if (opts.unsupportedCode) return opts.unsupportedCode;
  if (!selection) return '';
  if (selection.startsWith('Custom.')) return selection.split('.')[1] ?? '';
  const segments = selection.split('.');
  const deduped = segments.filter((s, i) => i === 0 || s !== segments[i - 1]);
  const def = withHiddenRuntimeActionArgs(findActionDef(selection));
  const actionName = deduped[deduped.length - 1] ?? '';
  if (!def) {
    const args = Object.entries(argValues || {})
      .filter(([argName]) => String(argName || '').trim())
      .map(([argName, rawValue]) => {
        const trimmedValue = String(rawValue ?? '').trim();
        if (/^arg\d+$/.test(argName)) return trimmedValue;
        return formatKnownActionArg(
          { name: argName, type: '' },
          trimmedValue,
          shouldQuotePythonActionArg({ name: argName, type: '' }, actionName),
          actionName,
          getRawActionArgValue(argValues, argName)
        );
      })
      .join(', ');
    return `${deduped.join('.')}(${args})`;
  }
  if (!def?.arguments) return `${deduped.join('.')}()`;
  const defArguments = def.arguments as FrameworkArgument[];
  const knownArgNames = new Set(defArguments.map((arg) => String(arg.name || '').trim()).filter(Boolean));
  const knownArgsByName = new Map<string, FrameworkArgument>();
  defArguments.forEach((arg) => {
    const name = String(arg.name || '').trim();
    if (name) knownArgsByName.set(name, arg);
  });
  const emittedKeys = new Set<string>();

  const formatSchemaArg = (arg: FrameworkArgument, index: number, sourceKey?: string): string | null => {
    const positionalKey = `arg${index}`;
    if (isTupleArg(arg)) return formatTupleActionArg(arg, argValues, def.name, index);
    if (isSequenceTupleArg(arg)) {
      const hasRows = sequenceTupleRowIndices(arg.name, argValues ?? {}).length > 0;
      // Preserve an unsupported raw expression (e.g. `get_lines()`) verbatim instead of dropping it.
      const rawValue = String((argValues ?? {})[arg.name] ?? '').trim();
      if (!hasRows && rawValue) return `${arg.name}=${rawValue}`;
      // Rows present, or zero rows: emit the list literal (`[]` for zero rows) so a required
      // sequence argument is never omitted — matching the controller serializer.
      return `${arg.name}=${formatSequenceTupleArgValue(arg, argValues ?? {}, def.name)}`;
    }

    const sourceHasValue = sourceKey ? Object.prototype.hasOwnProperty.call(argValues ?? {}, sourceKey) : false;
    const hasNamedValue = Object.prototype.hasOwnProperty.call(argValues ?? {}, arg.name);
    const hasPositionalValue = Object.prototype.hasOwnProperty.call(argValues ?? {}, positionalKey);
    if (!hasNamedValue && !hasPositionalValue && !sourceHasValue) return null;

    const sourceValueKey = hasNamedValue ? arg.name : sourceHasValue && sourceKey ? sourceKey : positionalKey;
    const rawValue = String((argValues ?? {})[sourceValueKey] ?? '').trim();
    const rawSourceValue = getRawActionArgValue(argValues, sourceValueKey);
    if (rawValue.length === 0 && isFrameworkArgOptionalNoneDefault(arg)) return null;
    const shouldQuote = shouldQuotePythonActionArg(arg, def.name);
    return formatKnownActionArg(arg, rawValue, shouldQuote, def.name, rawSourceValue);
  };

  const formatOrderedArg = (argName: string): string[] => {
    const normalized = String(argName || '').trim();
    if (!normalized || emittedKeys.has(normalized)) return [];
    const positionalMatch = normalized.match(/^arg(\d+)$/);
    const positionalIndex = positionalMatch ? Number(positionalMatch[1]) : -1;
    const schemaArg = positionalIndex >= 0 ? defArguments[positionalIndex] : knownArgsByName.get(normalized);
    if (schemaArg) {
      const schemaIndex = positionalIndex >= 0 ? positionalIndex : defArguments.indexOf(schemaArg);
      const formatted = formatSchemaArg(schemaArg, schemaIndex, normalized);
      emittedKeys.add(normalized);
      emittedKeys.add(schemaArg.name);
      return formatted ? [formatted] : [];
    }
    if (!PYTHON_IDENTIFIER_RE.test(normalized) || !Object.prototype.hasOwnProperty.call(argValues ?? {}, normalized)) return [];
    emittedKeys.add(normalized);
    return [formatUnknownActionArg(normalized, argValues[normalized], argValues, def.name)];
  };

  const orderedArgs = getRawActionArgOrder(argValues).flatMap(formatOrderedArg);
  const knownArgs = defArguments.flatMap((arg, index) => {
    const positionalKey = `arg${index}`;
    if (emittedKeys.has(arg.name) || emittedKeys.has(positionalKey)) return [];
    const formatted = formatSchemaArg(arg as FrameworkArgument, index);
    if (!formatted) return [];
    emittedKeys.add(arg.name);
    emittedKeys.add(positionalKey);
    return [formatted];
  });
  const unknownArgs = Object.entries(argValues || {})
    .filter(([argName]) => {
      const normalized = String(argName || '').trim();
      return PYTHON_IDENTIFIER_RE.test(normalized) && !knownArgNames.has(normalized) && !/^arg\d+$/.test(normalized) && !emittedKeys.has(normalized);
    })
    .map(([argName, rawValue]) => formatUnknownActionArg(argName, rawValue, argValues, def.name));
  const args = [...orderedArgs, ...knownArgs, ...unknownArgs].join(', ');
  return `${deduped.join('.')}(${args})`;
}

export function formatAutonomousActionExpression(
  selection: string,
  argValues: Record<string, string>,
  findActionDef: (label: string) => FrameworkAction | null,
  opts: { unsupportedCode?: string } = {}
): string {
  return formatActionExpression(selection, argValues, findActionDef, opts);
}

export function humanizeRoutineIdentifier(identifier: string): string {
  return String(identifier || '')
    .replace(/^_+|_+$/g, '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function toPythonIdentifier(raw: string, fallback = 'autonomous_routine'): string {
  const normalized = String(raw || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^A-Za-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
  const candidate = normalized || fallback;
  const safe = /^[A-Za-z_]/.test(candidate) ? candidate : `routine_${candidate}`;
  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(safe)) return fallback;
  return PYTHON_RESERVED_WORDS.has(safe) ? `routine_${safe}` : safe;
}

export function makeUniqueRoutineIdentifier(raw: string, existing: Set<string>): string {
  const base = toPythonIdentifier(raw);
  if (!existing.has(base)) return base;
  let index = 2;
  while (existing.has(`${base}_${index}`)) index += 1;
  return `${base}_${index}`;
}

export function collectTopLevelPythonIdentifiers(code: string): Set<string> {
  const identifiers = new Set<string>();
  const lines = String(code || '').split(/\r?\n/);
  lines.forEach((line) => {
    if (!line.trim() || leadingWhitespace(line).length > 0) return;
    const assignmentMatch = line.match(/^([A-Za-z_][\w]*)\s*(?::[^=]+)?=/);
    if (assignmentMatch?.[1]) identifiers.add(assignmentMatch[1]);
    const defMatch = line.match(/^def\s+([A-Za-z_][\w]*)\s*\(/);
    if (defMatch?.[1]) identifiers.add(defMatch[1]);
    const classMatch = line.match(/^class\s+([A-Za-z_][\w]*)\b/);
    if (classMatch?.[1]) identifiers.add(classMatch[1]);
    const startReceiverMatch = line.match(/^([A-Za-z_][\w]*)(?:\s*\(\s*\))?\s*\.\s*start\s*\(/);
    if (startReceiverMatch?.[1]) identifiers.add(startReceiverMatch[1]);
  });
  return identifiers;
}

function isSupportedGroupType(value: string): value is AutonomousGroupType {
  return (AUTONOMOUS_GROUP_TYPES as readonly string[]).includes(value);
}

function leadingWhitespace(value: string): string {
  return value.match(/^\s*/)?.[0] ?? '';
}

function countCharOutsideStrings(value: string, charToCount: string): number {
  let count = 0;
  let quote: '"' | "'" | null = null;
  let escaped = false;
  for (const char of value) {
    if (escaped) {
      escaped = false;
      continue;
    }
    if (char === '\\') {
      escaped = true;
      continue;
    }
    if (quote) {
      if (char === quote) quote = null;
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      continue;
    }
    if (char === charToCount) count += 1;
  }
  return count;
}

function collectReturnExpression(lines: string[], returnLineIndex: number): { expression: string; endLineIndex: number } | null {
  const line = lines[returnLineIndex] ?? '';
  const returnIndex = line.indexOf('return');
  if (returnIndex < 0) return null;

  return collectExpressionFromChunk(lines, returnLineIndex, line.slice(returnIndex + 'return'.length).trim());
}

function collectExpressionFromChunk(
  lines: string[],
  startLineIndex: number,
  firstExpressionChunk: string
): { expression: string; endLineIndex: number } | null {
  const firstChunk = firstExpressionChunk.trim();
  let explicitContinuation = firstChunk.endsWith('\\');
  const chunks: string[] = [firstChunk.replace(/\\$/, '').trim()];
  let depth = countCharOutsideStrings(chunks[0], '(') - countCharOutsideStrings(chunks[0], ')');
  let endLineIndex = startLineIndex;

  while ((depth > 0 || explicitContinuation) && endLineIndex + 1 < lines.length) {
    endLineIndex += 1;
    const next = lines[endLineIndex] ?? '';
    const nextTrimmed = next.trim();
    explicitContinuation = nextTrimmed.endsWith('\\');
    chunks.push(nextTrimmed.replace(/\\$/, '').trim());
    depth += countCharOutsideStrings(next, '(') - countCharOutsideStrings(next, ')');
  }

  const expression = chunks.join(' ').replace(/\s+/g, ' ').trim();
  return expression ? { expression, endLineIndex } : null;
}

function parseCallExpression(value: string): { callee: string; argsText: string } | null {
  const trimmed = value.trim();
  const match = trimmed.match(/^([A-Za-z_][\w]*(?:\.[A-Za-z_][\w]*)*)\s*\(([\s\S]*)\)\s*$/);
  if (!match?.[1]) return null;
  return { callee: match[1], argsText: match[2] ?? '' };
}

export function parseAutonomousRoutineStep(expression: string): AutonomousRoutineStep | null {
  const call = parseCallExpression(expression);
  if (!call) return null;
  const calleeName = call.callee.split('.').pop() ?? call.callee;
  if (isSupportedGroupType(calleeName)) {
    const childExpressions = call.argsText.trim().length
      ? splitTopLevelByComma(call.argsText)
          .map((entry) => entry.trim())
          .filter(Boolean)
      : [];
    const children = childExpressions.flatMap((entry) => {
      const parsed = parseAutonomousRoutineStep(entry);
      return parsed ? [parsed] : [];
    });
    if (children.length !== childExpressions.length) return null;
    return { kind: 'group', groupType: calleeName, children };
  }
  const parsedAction = parseActionExpression(expression);
  return parsedAction.selection ? { kind: 'action', selection: parsedAction.selection, argValues: parsedAction.argValues } : null;
}

export function formatAutonomousRoutineStep(
  step: AutonomousRoutineStep,
  findActionDef: (label: string) => FrameworkAction | null,
  indent = '        '
): string {
  if (step.kind === 'action') {
    return formatGeneratedPythonCall(formatActionExpression(step.selection, step.argValues, findActionDef))
      .split('\n')
      .map((line) => `${indent}${line}`)
      .join('\n');
  }

  const childIndent = `${indent}    `;
  const children = step.children.map((child) => `${formatAutonomousRoutineStep(child, findActionDef, childIndent)},`);
  if (children.length === 0) return `${indent}${step.groupType}()`;
  return [`${indent}${step.groupType}(`, ...children, `${indent})`].join('\n');
}

export function formatAutonomousRoutineAssignment(
  identifier: string,
  composition: AutonomousRoutineStep,
  findActionDef: (label: string) => FrameworkAction | null
): string {
  const expressionLines = formatAutonomousRoutineStep(composition, findActionDef, '').split('\n');
  const [firstLine = '', ...restLines] = expressionLines;
  return [`${identifier} = ${firstLine.trimStart()}`, ...restLines, ''].join('\n');
}

export function getAutonomousRoutineActionExpression(routine: AutonomousRoutineOption | null | undefined): string {
  if (!routine) return '';
  if (routine.referenceExpression) return routine.referenceExpression;
  if (!routine.identifier) return routine.expression;
  return `${routine.identifier}()`;
}

function findAutonomousRoutineRegionSpan(lines: string[]): { startLineIndex: number; endLineIndex: number } | null {
  const span = findRegionSpanLineIndices(lines, isAutonomousRoutinesRegionStartLine);
  if (!span) return null;
  return { startLineIndex: span.startLine, endLineIndex: span.endLine };
}

function isLineInsideAutonomousRoutineRegion(lineIndex: number, span: { startLineIndex: number; endLineIndex: number } | null): boolean {
  return Boolean(span && lineIndex > span.startLineIndex && lineIndex < span.endLineIndex);
}

function cleanEmptyAutonomousRoutineRegion(code: string): string {
  const lines = String(code || '').split(/\r?\n/);
  const span = findAutonomousRoutineRegionSpan(lines);
  if (!span || !isRegionInteriorEmpty(lines, { startLine: span.startLineIndex, endLine: span.endLineIndex })) return code;
  const nextLines = [...lines.slice(0, span.startLineIndex), ...lines.slice(span.endLineIndex + 1)];
  return nextLines.join('\n').replace(/\n{3,}/g, '\n\n');
}

export function scanAutonomousRoutinesFromCode(code: string): AutonomousRoutineOption[] {
  const lines = String(code || '').split(/\r?\n/);
  const regionSpan = findAutonomousRoutineRegionSpan(lines);
  const routines: AutonomousRoutineOption[] = [];

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex] ?? '';
    const assignmentMatch = line.match(/^(\s*)([A-Za-z_][\w]*)\s*=\s*(.+)$/);
    if (assignmentMatch?.[2]) {
      if ((assignmentMatch[1] ?? '').length > 0) continue;
      const identifier = assignmentMatch[2];
      const insideGeneratedRegion = isLineInsideAutonomousRoutineRegion(lineIndex, regionSpan);
      const result = collectExpressionFromChunk(lines, lineIndex, assignmentMatch[3] ?? '');
      const composition = result ? parseAutonomousRoutineStep(result.expression) : null;
      const roundTrippable = Boolean(composition);
      if (result && composition && (insideGeneratedRegion || composition.kind === 'group')) {
        routines.push({
          id: identifier,
          identifier,
          label: humanizeRoutineIdentifier(identifier) || identifier,
          source: insideGeneratedRegion && roundTrippable ? 'ui-owned' : 'code-only',
          editable: insideGeneratedRegion && roundTrippable,
          deletable: insideGeneratedRegion && roundTrippable,
          duplicable: roundTrippable,
          composition,
          expression: result.expression,
          referenceExpression: identifier,
          startLineIndex: lineIndex,
          endLineIndex: result.endLineIndex,
        });
        lineIndex = result.endLineIndex;
        continue;
      }
    }

    const defMatch = line.match(/^(\s*)def\s+([A-Za-z_][\w]*)\s*\(([^)]*)\)\s*:/);
    if (!defMatch?.[2]) continue;
    if ((defMatch[1] ?? '').length > 0) continue;
    if (String(defMatch[3] ?? '').trim()) continue;

    const defIndent = defMatch[1] ?? '';
    const identifier = defMatch[2];
    let endLineIndex = lineIndex;
    for (let scanIndex = lineIndex + 1; scanIndex < lines.length; scanIndex += 1) {
      const scanLine = lines[scanIndex] ?? '';
      if (scanLine.trim() && leadingWhitespace(scanLine).length <= defIndent.length) break;
      endLineIndex = scanIndex;
    }

    const bodyIndentLength = lines
      .slice(lineIndex + 1, endLineIndex + 1)
      .find((bodyLine) => bodyLine.trim() && leadingWhitespace(bodyLine).length > defIndent.length)
      ?.match(/^\s*/)?.[0].length;
    let returnExpression = '';
    for (let scanIndex = lineIndex + 1; scanIndex <= endLineIndex; scanIndex += 1) {
      const bodyLine = lines[scanIndex] ?? '';
      if (bodyIndentLength === undefined || leadingWhitespace(bodyLine).length !== bodyIndentLength || !/^\s*return\b/.test(bodyLine)) continue;
      const result = collectReturnExpression(lines, scanIndex);
      if (result) {
        returnExpression = result.expression;
      }
      break;
    }

    if (!returnExpression) continue;

    const insideGeneratedRegion = isLineInsideAutonomousRoutineRegion(lineIndex, regionSpan);
    const composition = parseAutonomousRoutineStep(returnExpression);
    const roundTrippable = Boolean(composition);

    routines.push({
      id: identifier,
      identifier,
      label: humanizeRoutineIdentifier(identifier) || identifier,
      source: insideGeneratedRegion && roundTrippable ? 'ui-owned' : 'code-only',
      editable: insideGeneratedRegion && roundTrippable,
      deletable: insideGeneratedRegion && roundTrippable,
      duplicable: roundTrippable,
      composition,
      expression: returnExpression,
      referenceExpression: `${identifier}()`,
      startLineIndex: lineIndex,
      endLineIndex,
    });
  }

  return routines;
}

export function replaceRoutineInCode(code: string, routine: AutonomousRoutineOption, replacement: string): string {
  const lines = String(code || '').split(/\r?\n/);
  const nextLines = [...lines.slice(0, routine.startLineIndex), replacement.trimEnd(), ...lines.slice(routine.endLineIndex + 1)];
  return nextLines.join('\n').replace(/\n{3,}/g, '\n\n');
}

export function deleteRoutineFromCode(code: string, routine: AutonomousRoutineOption): string {
  const lines = String(code || '').split(/\r?\n/);
  const nextLines = [...lines.slice(0, routine.startLineIndex), ...lines.slice(routine.endLineIndex + 1)];
  return cleanEmptyAutonomousRoutineRegion(nextLines.join('\n')).replace(/\n{3,}/g, '\n\n');
}

export function appendRoutineToCode(code: string, routineSource: string): string {
  const text = String(code || '').trimEnd();
  const routineText = routineSource.trimEnd();
  if (!text) return `${UI_AUTONOMOUS_ROUTINES_REGION_START}\n${routineText}\n${UI_AUTONOMOUS_ROUTINES_REGION_END}\n`;

  const lines = text.split(/\r?\n/);
  const regionSpan = findAutonomousRoutineRegionSpan(lines);
  if (regionSpan) {
    const regionBody = lines
      .slice(regionSpan.startLineIndex + 1, regionSpan.endLineIndex)
      .join('\n')
      .trim();
    const nextRegionLines = [
      lines[regionSpan.startLineIndex],
      [regionBody, routineText].filter(Boolean).join('\n\n'),
      lines[regionSpan.endLineIndex],
    ].filter((entry) => entry !== undefined && entry !== '');
    return [...lines.slice(0, regionSpan.startLineIndex), ...nextRegionLines, ...lines.slice(regionSpan.endLineIndex + 1)].join('\n') + '\n';
  }

  const routineBlock = `${UI_AUTONOMOUS_ROUTINES_REGION_START}\n${routineText}\n${UI_AUTONOMOUS_ROUTINES_REGION_END}`;
  const matchedIndex = lines.findIndex(
    (line) =>
      /^\s*UtilityActions\s*\.\s*add_action_at_program_start\s*\(/.test(line) || /^\s*[A-Za-z_][\w]*(?:\s*\(\s*\))?\s*\.\s*start\s*\(/.test(line)
  );
  if (matchedIndex < 0) return `${text}\n\n${routineBlock}\n`;

  const matchedIndentLength = leadingWhitespace(lines[matchedIndex] ?? '').length;
  let insertIndex = matchedIndex;
  if (matchedIndentLength > 0) {
    for (let scanIndex = matchedIndex - 1; scanIndex >= 0; scanIndex -= 1) {
      const scanLine = lines[scanIndex] ?? '';
      if (!scanLine.trim()) continue;
      if (leadingWhitespace(scanLine).length < matchedIndentLength) {
        insertIndex = scanIndex;
        break;
      }
    }
  }

  const before = lines.slice(0, insertIndex).join('\n').trimEnd();
  const after = lines.slice(insertIndex).join('\n').trimEnd();
  return [before, routineBlock, after].filter(Boolean).join('\n\n') + '\n';
}
